'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["dialogTitle", "dialogTitleId", "id", "body", "footer", "legal", "legalStrict", "className", "colorSet", "aria-label"];
import React, { useState } from 'react';
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { addColorSet } from "../../styles/index.mjs";
import { Body } from "./Body.mjs";
import { Dialog } from "./Dialog.mjs";
import { Footer } from "./Footer.mjs";
import { Legal } from "./Legal.mjs";
import { LegalContainer } from "./LegalContainer.mjs";
import { Title } from "./Title.mjs";
import { warn } from "../../logging.mjs";
/**
* **DialogConfirmation** ![Status: Production](https://img.shields.io/badge/PRODUCTION-%2357B560|height=14)
*
* [GitHub](https://ghe.spotify.net/encore/web/tree/master/src/core/components/DialogConfirmation) | [Figma](https://www.figma.com/file/bnQXqGkLeiTlbk8JXGDcsX/Encore-Web---Light-Theme-Toolkit?node-id=0:1208)
*
* DialogConfirmation requires that the user confirm the information and uses a primary button.
*
* [Read the full component documentation →](https://encore.spotify.net/web/components/dialog?format=figma-figma#dialogconfirmation)
*
* @example
* () => (
*  <DialogConfirmation
*    dialogTitle="Title"
*    id="dialogConfirmation"
*    body={
*      <div>
*        <Type as="p">
*          Aenean lacinia bibendum nulla sed consectetur. Cras justo odio, dapibus ac facilisis in,
*          egestas eget quam. Duis mollis, est non commodo luctus.
*        </Type>
*        <Type as="p">
*          Etiam porta sem malesuada magna mollis euismod. Lorem ipsum dolor sit amet, consectetur
*          adipiscing elit.
*        </Type>
*      </div>
*    }
*    footer={
*      <div>
*        <ButtonTertiary size="small" condensed>
*          Cancel
*        </ButtonTertiary>
*        <ButtonPrimary size="small">Accept</ButtonPrimary>
*      </div>
*    }
*  />
*);
*
*/
export var DialogConfirmation = /*#__PURE__*/React.forwardRef(function DialogConfirmation(_ref, ref) {
  var dialogTitle = _ref.dialogTitle,
    dialogTitleId = _ref.dialogTitleId,
    id = _ref.id,
    body = _ref.body,
    footer = _ref.footer,
    legal = _ref.legal,
    legalStrict = _ref.legalStrict,
    className = _ref.className,
    _ref$colorSet = _ref.colorSet,
    colorSet = _ref$colorSet === void 0 ? 'base' : _ref$colorSet,
    ariaLabel = _ref['aria-label'],
    props = _objectWithoutProperties(_ref, _excluded);
  var titleId = id && "".concat(id, "-title") || dialogTitleId;
  if (!(dialogTitle && titleId || ariaLabel)) {
    warn('Warning: The DialogConfirmation component must have dialogTitle and an id or an aria-label to be accessible.');
  }
  var bodyId = id && "".concat(id, "-body");
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasBorder = _useState2[0],
    setHasBorder = _useState2[1];
  return /*#__PURE__*/React.createElement(Dialog, Object.assign({
    ref: ref,
    id: id,
    colorSet: colorSet,
    className: addColorSet(colorSet, className),
    "aria-label": ariaLabel,
    "aria-describedby": bodyId,
    "aria-labelledby": !ariaLabel && dialogTitle ? titleId : undefined
  }, props, {
    "data-encore-id": ENCORE_DATA_IDS.DialogConfirmation
  }), dialogTitle && /*#__PURE__*/React.createElement(Title, {
    id: titleId,
    hasBorder: hasBorder,
    colorSet: colorSet,
    "data-testid": "dialogTitle"
  }, dialogTitle), /*#__PURE__*/React.createElement(Body, {
    id: bodyId,
    colorSet: colorSet,
    onScroll: function onScroll(e) {
      return setHasBorder(e.target instanceof HTMLElement && e.target.scrollTop !== 0);
    }
  }, body), (footer || legal) && /*#__PURE__*/React.createElement(Footer, null, legal && /*#__PURE__*/React.createElement(LegalContainer, {
    legalStrict: legalStrict
  }, /*#__PURE__*/React.createElement(Legal, {
    colorSet: colorSet,
    legalStrict: legalStrict
  }, legal)), footer));
});